import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {FormConfiguration} from "../../../advanced-form/form-configuration.type";
import {BulkCommand} from "../../../commands/bulk.command";
import {CreateKeywordCommand} from "../../../commands/create-keyword.command";
import {ICommand} from "../../../commands/i-command.interface";
import {RecolorKeywordCommand} from "../../../commands/recolor-keyword.command";
import {RenameKeywordCommand} from "../../../commands/rename-keyword.command";
import {Keyword} from "../../../domain/entities/keyword.entity";
import {AbstractEntityDialogComponent} from "../../ha-dialog/abstract-entity-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogCommand} from "../../ha-dialog/dialog-command.interface";
import {keywordFormSettings} from "./keyword-form-settings.const";
import {get, translate} from "lit-translate";

@customElement(`ha-dialog-keyword-edit`)
export class HaDialogKeywordEditComponent extends AbstractEntityDialogComponent<Keyword> {

    public title = get('ha-boards-view.edit-label.header');

    protected _formSettings: FormConfiguration<Keyword>[] = keywordFormSettings;

    protected _renderMainContent(): unknown {
        const bindTo = this.form!.bindToFn;

        return html`
            <ha-form-keyword>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <ha-input-text class="max9999-flexrow" data-type="title" label=${translate('components.ha-dialog.title')}
                                    ${bindTo(`key`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-text>
                    <ha-input-text class="max9999-flexrow" data-type="shortcut"
                                    label=${translate('components.ha-dialog.shortcut')}
                                    maxlength="10"
                                    ${bindTo(`shortcut`)}
                    ></ha-input-text>
                    <ha-input-color class="max9999-flexrow" data-type="color"
                                    label=${translate('components.ha-dialog.color')}
                                    .entity=${Keyword}
                                    ${bindTo(`color`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-color>
                    ${this._renderTextColorField(this.form!.value.key ?? ``)}
                </main>
            </ha-form-keyword>
        `;
    }

    protected _renderFormFooter(): unknown {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker secondary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickResetButton}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    private _onClickSaveButton(): void {
        const data = this.form!.getChangedValues();

        if (!this._entity) {
            return;
        }

        Object.assign(this._entity, data);

        this.dispatchCommand(<DialogCommand<ICommand<Keyword>>>{
            command: DialogCommandName.OKAY,
            payload: this._entity.isNew()
                ? new CreateKeywordCommand(this._entity)
                : this._buildUpdateCommands(this._entity)
        });
    }

    private _onClickResetButton(): void {
        this.form?.reset();
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog. */
    private _onClickSaveAndCloseButton(): void {
        this._onClickSaveButton();
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }

    private _buildUpdateCommands(entity: Keyword): BulkCommand {
        // Mixed payloads since the Recolor commands carry a RecolorContent (feature plan 0074, ADR-016).
        const commands: ICommand[] = [];
        // One Recolor carries whichever of colour and text colour changed (feature plan 0074).
        if (entity.changedValues.includes(`color`) || entity.changedValues.includes(`textColor`)) {
            commands.push(new RecolorKeywordCommand(entity, entity.changedValues));
        }
        if (entity.changedValues.includes(`key`) || entity.changedValues.includes(`shortcut`)) {
            commands.push(new RenameKeywordCommand(entity));
        }

        return new BulkCommand(commands);
    }
}